import { useMemo } from "react";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import Avatar from "../../../components/Avatar";
import { renderUserName } from "../../../utils/helpers/user";
import { getUploadUrl } from "../../../utils/helpers/upload";
import { useCompanyDateFormat } from "../../../utils/hooks/company";

function FacePhoto({ label, path, alt }) {
	return (
		<div className="d-flex flex-column align-items-center gap-2">
			<span className="text-xs font-weight-bold text-muted">{label}</span>
			{path ? (
				<img
					src={getUploadUrl(path)}
					alt={alt}
					style={{
						width: 180,
						height: 180,
						borderRadius: 12,
						objectFit: "cover",
						border: "1px solid #D5D5D1",
					}}
				/>
			) : (
				<div
					className="d-flex align-items-center justify-content-center text-muted"
					style={{
						width: 180,
						height: 180,
						borderRadius: 12,
						border: "1px dashed #D5D5D1",
					}}
				>
					<i className="fa-solid fa-image" style={{ fontSize: "28px" }} />
				</div>
			)}
		</div>
	);
}

function FaceRecognitionRequestView({ values, team }) {
	const { t } = useTranslation();
	const dateFormat = useCompanyDateFormat();

	const data = useMemo(() => values?.data || {}, [values?.data]);
	const isMismatch = values?.type === "face-mismatch";

	const meta = useMemo(() => {
		const rows = [];
		if (data.livenessPassed != null) {
			rows.push([t("liveness"), data.livenessPassed ? t("yes") : t("no")]);
		}
		if (data.livenessScore != null) {
			rows.push([t("liveness-score"), Number(data.livenessScore).toFixed(2)]);
		}
		if (data.deviceId) {
			rows.push([t("device"), data.deviceId]);
		}
		if (data.requestCount != null) {
			rows.push([t("request-count"), data.requestCount]);
		}
		if (data.temporaryFaceValidUntil) {
			rows.push([
				t("valid-until"),
				moment(data.temporaryFaceValidUntil).format(dateFormat),
			]);
		}
		if (data.approvalExpiresAt) {
			rows.push([
				t("approval-expires-at"),
				moment(data.approvalExpiresAt).format(dateFormat),
			]);
		}
		return rows;
	}, [data, dateFormat, t]);

	const note = data.note || values?.approveReason;

	return (
		<div className="rounded p-4 mb-5" style={{ border: "1px dashed #D5D5D1" }}>
			{team && (
				<div className="d-flex align-items-center gap-3 mb-4">
					<Avatar user={values?.user} size={48} txtStyle={{ fontSize: "10px" }} />
					<div className="d-flex flex-column">
						<span className="text-dark font-weight-bold">
							{renderUserName(values?.user)}
						</span>
						{(values?.user?.employeeNumber || data.employeeNumber) && (
							<span className="text-xs text-muted">
								{t("employee-number")}:{" "}
								{values?.user?.employeeNumber || data.employeeNumber}
							</span>
						)}
					</div>
				</div>
			)}

			<div className="d-flex flex-wrap gap-6 justify-content-center mb-4">
				<FacePhoto
					label={t("new-capture")}
					path={data.verificationPhoto}
					alt={t("new-capture")}
				/>
				{isMismatch && (
					<FacePhoto
						label={t("current-approved-face")}
						path={data.existingEnrollmentPhotoReference}
						alt={t("current-approved-face")}
					/>
				)}
			</div>

			{meta.length > 0 && (
				<div className="d-flex flex-column gap-1">
					{meta.map(([label, value]) => (
						<div key={label} className="d-flex align-items-center">
							<span className="font-weight-bold">{label}</span>
							<span className="mx-2">{value}</span>
						</div>
					))}
				</div>
			)}

			{note && (
				<div className="mt-2">
					<span className="font-weight-bold">{t("note")}</span>
					{": "}
					{note}
				</div>
			)}
		</div>
	);
}

export default FaceRecognitionRequestView;
